<template>
  <section
    class="order-statement"
    aria-label="Statement"
    data-testid="admin-order-statement"
  >
    <table class="order-statement__table">
      <caption class="order-statement__hidden">
        Order statement
      </caption>
      <thead class="order-statement__hidden">
        <tr>
          <th scope="col">Description</th>
          <th scope="col">Amount</th>
          <th v-if="paymentsLinkable" scope="col">Actions</th>
        </tr>
      </thead>
      <tbody>
        <tr
          v-for="line in lines"
          :key="line.testid"
          :class="[
            'order-statement__line',
            'order-statement__line--' + line.kind,
            { 'order-statement__line--struck': line.struck },
          ]"
          :data-testid="line.testid"
        >
          <th scope="row" class="order-statement__label">
            <span class="order-statement__name">{{ line.label }}</span>
            <span v-if="line.tag" class="order-statement__tag">{{
              line.tag
            }}</span>
            <span
              v-for="(detail, index) in line.details"
              :key="index"
              class="order-statement__detail"
              >{{ detail }}</span
            >
          </th>
          <!-- admin-order-amount-owed predates this layout; the Playwright specs read it. -->
          <td
            class="order-statement__amount"
            :data-testid="
              line.kind === 'balance' ? 'admin-order-amount-owed' : null
            "
          >
            <span class="order-statement__figure">{{ line.amount }}</span>
          </td>
          <td v-if="paymentsLinkable" class="order-statement__action">
            <button
              v-if="line.paymentId !== null"
              type="button"
              class="order-statement__open"
              data-testid="void-payment-button"
              :aria-label="openPaymentLabel(line)"
              @click="$emit('view-payment', { payment_id: line.paymentId })"
            >
              <span class="order-statement__open-text">Refund or void</span>
              <svg
                class="order-statement__open-icon"
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
                aria-hidden="true"
              >
                <path d="M9 6l6 6-6 6" />
              </svg>
            </button>
          </td>
        </tr>
      </tbody>
    </table>
  </section>
</template>

<script>
export default {
  name: "OrderStatement",
  props: {
    // statementLines() from orderView.ts
    lines: { type: Array, required: true },
    // Only the office can refund or void; the instructor host has no payment page to open.
    paymentsLinkable: { type: Boolean, default: false },
  },
  methods: {
    openPaymentLabel(line) {
      const when = line.details.length > 0 ? ` from ${line.details[0]}` : "";
      return `Refund or void the ${line.label} payment${when}`;
    },
  },
};
</script>

<style lang="scss" scoped>
@import "../../../../assets/css/partials/variables.scss";

.order-statement {
  padding: 4px 16px 16px;
  border: 1px solid $light;
  border-radius: 4px;
  background: $white;
  color: $grey;

  @media #{$sevensixeight} {
    padding: 8px 28px 24px;
  }
}

.order-statement__hidden {
  position: absolute;
  width: 1px;
  height: 1px;
  overflow: hidden;
  clip: rect(0 0 0 0);
  white-space: nowrap;
}

.order-statement__table {
  width: 100%;
  border-collapse: collapse;
  font-variant-numeric: tabular-nums;

  th,
  td {
    padding: 4px 0;
    vertical-align: middle;
    font-weight: normal;
    text-align: left;
  }
}

.order-statement__label {
  min-width: 0;
}

.order-statement__name {
  font-size: 14px;
}

.order-statement__tag {
  display: inline-block;
  margin-left: 8px;
  padding: 1px 8px;
  border-radius: 10px;
  background: $light;
  font-size: 12px;
  font-weight: 600;
  vertical-align: 1px;
}

.order-statement__detail {
  display: block;
  font-size: 13px;
  color: $blue-grey;
}

.order-statement__table .order-statement__amount {
  padding-left: 12px;
  font-size: 14px;
  text-align: right;
  white-space: nowrap;
}

.order-statement__table .order-statement__action {
  width: 28px;
  text-align: right;

  @media #{$nineninetwo} {
    width: 112px;
  }
}

.order-statement__open {
  display: inline-flex;
  align-items: center;
  justify-content: flex-end;
  min-width: 28px;
  min-height: 44px;
  padding: 0;
  border: 0;
  background: none;
  color: $green-ink;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;

  &:hover .order-statement__open-text {
    text-decoration: underline;
  }

  &:focus-visible {
    outline: 2px solid $green;
    outline-offset: 2px;
  }
}

.order-statement__open-text {
  display: none;

  @media #{$nineninetwo} {
    display: inline;
  }
}

.order-statement__open-icon {
  @media #{$nineninetwo} {
    display: none;
  }
}

// Line kinds, in statement order.

.order-statement__line--item {
  th,
  td {
    padding: 14px 0 12px;
    vertical-align: top;
  }

  .order-statement__name {
    font-size: 16px;
    font-weight: 600;
  }

  .order-statement__amount {
    font-size: 15px;
    font-weight: 600;
  }
}

.order-statement__line--subtotal,
.order-statement__line--total {
  th,
  td {
    padding: 12px 0 8px;
    border-top: 1px solid $light;
  }
}

.order-statement__line--total {
  .order-statement__name,
  .order-statement__amount {
    font-size: 15px;
    font-weight: 600;
  }
}

.order-statement__line--coupon th {
  padding-bottom: 10px;
}

.order-statement__line--payment,
.order-statement__line--adjustment {
  th,
  td {
    height: 44px;
  }
}

.order-statement__line--no-payments .order-statement__name {
  color: $blue-grey;
}

// Accounting convention: a rule above the balance, a double rule under it.
.order-statement__line--balance {
  th,
  td {
    padding: 14px 0 6px;
    border-top: 1px solid $grey;
    vertical-align: baseline;
  }

  .order-statement__name {
    font-size: 17px;
    font-weight: 700;
  }

  .order-statement__amount {
    font-size: 22px;
    font-weight: 700;
  }

  .order-statement__figure {
    padding-bottom: 2px;
    border-bottom: 3px double $grey;
  }
}

.order-statement__line--net-received {
  th,
  td {
    padding-top: 12px;
  }

  .order-statement__name,
  .order-statement__amount {
    font-size: 13px;
    color: $blue-grey;
  }
}

// A voided or failed line is listed but does not count toward the balance.
.order-statement__line--struck {
  .order-statement__name {
    color: $blue-grey;
  }

  .order-statement__amount {
    color: $blue-grey;
    text-decoration: line-through;
  }
}
</style>
